import React, { useState } from 'react';
import styled, { useTheme } from 'styled-components/macro';
import { Button } from 'react-covideo-common';
import { PiDotsThreeOutlineVerticalFill } from 'react-icons/pi';
import { ListDropdownMenu } from 'lib/components';
import { ICON_COLOR } from 'app/pages/library/boards/main/style';
import { MdDeleteForever } from 'react-icons/md';
import { useIsMutating } from 'react-query';
import { HiOutlineDuplicate } from 'react-icons/hi';
import { DeleteUIKitModal } from './modals/DeleteUIKitModal';
import { IUIKitsQueryParams } from 'lib/api/superadmin/uikits/useQueryUIKits';
import { useDuplicateUIKitMutation } from 'lib/api/superadmin/uikits/useDuplicateUIKitMutation';
import { buildImageField } from '../modal/LandingPageBuilderModal';

const UIKitActionsWrapper = styled.div`
  position: relative;
`;

export interface IUIKitActionItemsItems {
  id: number;
  params: IUIKitsQueryParams;
  scrollToTop: () => void;
  remove: <X = any>(index: number) => X | undefined;
  unshift: <X = any>(value: X) => number;
}

enum UKitModalValue {
  Delete = 'delete',
}

type UIKitModalType = UKitModalValue;

interface IUIKitActionItem {
  title: string;
  titleColor: string;
  icon: JSX.Element;
  onClick?: () => void;
}

export const UIKitActionItems = ({
  id,
  params,
  scrollToTop,
  remove,
  unshift,
}: IUIKitActionItemsItems) => {
  const theme = useTheme();
  const [modal, setModal] = useState<UIKitModalType | null>(null);
  const [menuOpenedForBoard, setMenuOpenedForBoard] = useState<number | null>(
    null
  );

  const { mutateAsync: duplicate, isLoading: isDuplicating } =
    useDuplicateUIKitMutation(params, scrollToTop);

  const isMutating = useIsMutating();
  const loading = isDuplicating || isMutating > 0;

  const handleOnDuplicate = async () => {
    const data = await duplicate(id);
    const newUIKit = {
      ...data,
      thumbnail: buildImageField(null, data.thumbnail),
    };
    unshift(newUIKit);
  };

  const actionItems: IUIKitActionItem[] = [
    {
      title: 'Duplicate UI Kit',
      titleColor: theme.colors.neutral[100],
      icon: <HiOutlineDuplicate color={ICON_COLOR} size={20} />,
      onClick: handleOnDuplicate,
    },
    {
      title: 'Delete UI Kit',
      titleColor: theme.colors.danger[100],
      icon: <MdDeleteForever color={theme.colors.danger[100]} size={20} />,
      onClick: () => setModal(UKitModalValue.Delete),
    },
  ];

  const closeModalHandler = () => {
    setModal(null);
  };

  return (
    <UIKitActionsWrapper>
      <Button
        icon={
          <PiDotsThreeOutlineVerticalFill
            onClick={() => setMenuOpenedForBoard(id)}
            color={theme.colors.neutral[100]}
            style={{ cursor: 'pointer' }}
            size={18}
          />
        }
        variant='transparent'
        size='small'
        disabled={loading}
      />
      <ListDropdownMenu
        isMenuOpen={menuOpenedForBoard === id}
        items={actionItems}
        id={id.toString()}
        setIsMenuOpen={() => setMenuOpenedForBoard(null)}
        extendStyles={{
          menuWrapper: {
            width: '260px',
            padding: '20px',
            right: 0,
          },
          menuItem: {
            justifyItems: 'flex-start',
          },
          itemIcon: {
            margin: '0px',
          },
          itemTitle: {
            fontWeight: 500,
            fontSize: 14,
          },
        }}
      />
      {modal === UKitModalValue.Delete && (
        <DeleteUIKitModal
          id={id}
          closeModalHandler={closeModalHandler}
          params={params}
          remove={remove}
        />
      )}
    </UIKitActionsWrapper>
  );
};
